/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTimeout } from 'react-use';
import styled, { keyframes } from 'styled-components';
import { LFC } from '../../../Legacy';
import { useStreamedListQuery, useStreamedTextQuery } from '../../../api/ApiStreaming';
import { Component } from '../../../concepts/objects';
import { IsolatedObjectManager } from '../../../concepts/objects/IsolatedObjectManager';
import { Icon } from '../../../concepts/resources/Icon';
import { Button, Colors, Input, Prompt } from '../../../ui';
import { FlexScroller, ScrollerInteral } from '../../../ui/Scrollers';
import { useResolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { Physical } from '../../canvas/entities/Physical';
import { Counter } from '../../common/Users';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../../popups/Popup';
import { AutoScene } from '../tabularImport/Scene';

const ChatFrame = styled.div`
    position: relative;
`;

const ChatIcon = styled.div<{ color?: string }>`
    position: absolute;
    top: 0;
    left: 0;
    padding: .375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${p => p.color ?? 'none'};
    color: white;
    border-radius: 0.5rem;
`;

const ChatThread = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 1rem;
    padding-left: 4rem;
    padding-top: .125rem;
`;

const chatMessageIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const ChatMessage = styled.div<{ pre?: boolean }>`
    padding: .5rem 1rem;
    border-radius: .5rem;
    background: ${Colors.borderLight};
    animation: ${chatMessageIn} 0.3s ease-out;

    ${p => p.pre && `
        white-space: pre-wrap;
    `}
`;

const ItemPreview = ({ item }: { item: any }) => {

    const iom = useMemo(() => new IsolatedObjectManager(), []);
    // const figures = [] as any[];

    // for (let i = 0; i < slice.length; i++) {
    //     const x = i % config.preview[0];
    //     const y = Math.floor(i / config.preview[0]);
    //     const f = iom.create(config.type);
    //     f.moveBy(f.size().multiply([x, y]).add(20));
    //     for (const [t, s] of params.mappings ?? []) {
    //         f.set(t.path, t.filter(slice[i][s.key]));
    //     }
    //     figures.push(f);
    // }

    const figure = useMemo(() => {
        const { type } = Component.describe(item.type);
        const inst = iom.create(type) as Physical;
        for (const key of Object.keys(item)) {
            if (key === 'type') continue;
            inst.set(key, item[key]);
        }
        return inst;
    }, [item]);

    return <Flexer relative width="16" height="12">
        <AutoScene objects={[figure]} />
    </Flexer>;

};

export const importDataViaLanguageModelPromptPopup = (fileUrl: string) => ({ ui: ImportDataViaLanguageModelPromptPopup, args: { fileUrl } });

export const ImportDataViaLanguageModelPromptPopup: LFC<PopupProps> = ({ args, action, dismiss }) => {
    const fileUrl = args.fileUrl as string;

    const scroller = useRef<ScrollerInteral>();

    const [message, setMessage] = useState('summary in sticky');
    const me = useResolve(ProfileModel);

    const [intro] = useTimeout(250);

    const [desc, triggerDesc] = useStreamedTextQuery<{ fileUrl: string }>('/llm.describe');
    const [items, triggerItems] = useStreamedListQuery<{ fileUrl: string, message: string }>('/llm.fileToObjects');

    useEffect(() => {
        if (intro()) triggerDesc({ fileUrl });
    }, [intro()]);

    const onSubmit = useCallback(() => {
        triggerItems({ fileUrl, message });
    }, [fileUrl, message]);

    useEffect(() => {
        if (scroller.current) {
            const se = scroller.current.getScrollElement();
            se.scrollTo(0, se.scrollHeight);
        }
    }, [items]);

    return <Prompt ignoreEnter onAction={dismiss}>
        <Popup w={80} h={60}>
            <Popup.Close onAction={dismiss} />
            <Popup.Title>AI Data Import</Popup.Title>
            <Flexer height="1"></Flexer>
            <Popup.Content>
                <FlexScroller internalRef={scroller}>
                    <ChatFrame>
                        <ChatIcon color={Colors.positive}>
                            <Icon src="deqo://icons/hero/chip" size="2" />
                        </ChatIcon>
                        <ChatThread>
                            {intro() &&
                                <ChatMessage>
                                    Hey there, please wait while I check your file...
                                </ChatMessage>
                            }
                            {desc &&
                                <ChatMessage pre>{desc}</ChatMessage>
                            }
                            {items && <>
                                <ChatMessage >
                                    <Flexer flex="row" gap="1" wrap="wrap">
                                        {items.map((x, i) =>
                                            <ItemPreview key={i} item={x} />
                                        )}
                                    </Flexer>
                                </ChatMessage>
                            </>}
                        </ChatThread>
                    </ChatFrame>
                </FlexScroller>
                <Flexer shrink>
                    <ChatFrame style={{ marginTop: 'auto' }}>
                        <ChatIcon>
                            <Counter size={2.5} unit="rem" photo={me.photo} color={me.color}>
                                {me.initials}
                            </Counter>
                        </ChatIcon>
                        <Flexer margin="0 0 0 4rem">
                            <Input multiline={{ resize: 'none', rows: 3 }} value={message} onChange={setMessage} onApply={onSubmit} />
                        </Flexer>
                    </ChatFrame>
                </Flexer>
            </Popup.Content>
            <Popup.Actions>
                <Button variant="primary" onAction={onSubmit}>Submit</Button>
                <Button onAction={dismiss}>Cancel</Button>
            </Popup.Actions>
        </Popup>
    </Prompt>;
};
